import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  StarIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import useEntityStore from '../../hooks/useEntityStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  fetchBenefitsDashboardData,
  computeTrainerEarnings,
  computeTrainerEnrollmentStats,
  computeTrainerPaymentDetails,
} from '../../utils/benefitsFinance';
import { BENEFITS_DASHBOARD_KEY } from '../../utils/queryKeys';

const TeachersList = ({ showAddForm, setShowAddForm }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(showAddForm || false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);

  // React à la prop showAddForm
  React.useEffect(() => {
    if (showAddForm) {
      setIsFormOpen(true);
      setShowAddForm(false); // Reset pour éviter la réouverture
    }
  }, [showAddForm, setShowAddForm]);

  const {
    items: trainersList,
    addItem: addTrainer,
    updateItem: updateTrainer,
    deleteItem: deleteTrainerItem,
    isLoading,
    error,
    refreshData,
  } = useEntityStore('trainers');

  const teachersData = React.useMemo(() => ({ data: trainersList }), [trainersList]);

  const { data: dashboardData } = useQuery(
    BENEFITS_DASHBOARD_KEY,
    fetchBenefitsDashboardData,
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );

  const payments = dashboardData?.payments || [];
  const courses = dashboardData?.courses || [];
  const batches = dashboardData?.batches || [];
  const enrollments = dashboardData?.enrollments || [];
  const commissionRate = dashboardData?.commissionRate ?? 0.4;

  const teacherEarnings = React.useMemo(
    () => computeTrainerEarnings(payments, courses, trainersList, batches, commissionRate),
    [payments, courses, trainersList, batches, commissionRate]
  );

  const teacherPaymentDetails = React.useMemo(
    () =>
      computeTrainerPaymentDetails(
        payments,
        courses,
        trainersList,
        batches,
        commissionRate
      ),
    [payments, courses, trainersList, batches, commissionRate]
  );

  const teacherRealCounts = React.useMemo(() => {
    const counts = {};
    for (const trainer of trainersList || []) {
      counts[trainer.id] = computeTrainerEnrollmentStats(
        trainer.id,
        courses,
        batches,
        enrollments
      );
    }
    return counts;
  }, [trainersList, courses, batches, enrollments]);

  const specializationsData = React.useMemo(() => {
    const teachers = teachersData?.data || [];
    if (!Array.isArray(teachers)) return { data: [] };
    
    const specializations = [...new Set(teachers.map(teacher => teacher.specialization).filter(Boolean))];
    return { data: specializations.map(spec => ({ name: spec, value: spec })) };
  }, [teachersData]);

  // Process teachers data with earnings and real counts
  const teachers = React.useMemo(() => {
    if (!teachersData?.data || !Array.isArray(teachersData.data)) return [];
    
    return teachersData.data.map(teacher => {
      const earnings = teacherEarnings[teacher.id] || 0;
      const realCounts = teacherRealCounts[teacher.id] || { students: 0, courses: 0 };
      
      return {
        id: teacher.id,
        name: teacher.name,
        photo: teacher.profileImage,
        specialty: teacher.specialization,
        experience: teacher.yearsOfExperience || 0,
        rating: teacher.rating || 4.0,
        students: realCounts.students,  // Use real count based on actual course assignments
        courses: realCounts.courses,    // Use real count based on actual course assignments
        earnings: earnings,
        status: teacher.status || 'active',
        email: teacher.email,
        phone: teacher.phone,
        bio: teacher.bio || 'Aucune biographie disponible',
        availability: teacher.availability?.split(',') || ['Non spécifié'],
        salary: teacher.salary || 0,
        joinDate: teacher.joinDate || new Date().toISOString().split('T')[0]
      };
    });
  }, [teachersData, teacherEarnings, teacherRealCounts]);

  // Filtrage des formateurs
  const filteredTeachers = Array.isArray(teachers) ? teachers
    .filter(teacher => teacher.name && teacher.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(teacher => statusFilter === 'all' || teacher.status === statusFilter)
    .filter(teacher => specialtyFilter === 'all' || teacher.specialty === specialtyFilter) : [];

  // Calculate summary statistics - MOVED BEFORE CONDITIONAL RETURNS
  const summaryStats = React.useMemo(() => {
    if (!Array.isArray(filteredTeachers)) {
      return {
        totalTeachers: 0,
        totalRevenue: 0,
        averageRevenue: 0,
        topEarner: null
      };
    }
    
    const totalTeachers = filteredTeachers.length;
    const totalRevenue = filteredTeachers.reduce((sum, teacher) => sum + (teacher.earnings || 0), 0);
    const averageRevenue = totalTeachers > 0 ? totalRevenue / totalTeachers : 0;
    const topEarner = filteredTeachers.reduce((top, teacher) => 
      (teacher.earnings || 0) > (top?.earnings || 0) ? teacher : top, null);
    
    return {
      totalTeachers,
      totalRevenue,
      averageRevenue,
      topEarner
    };
  }, [filteredTeachers]);

  const summaryCards = React.useMemo(() => {
    const linkedPayments = Object.values(teacherPaymentDetails || {}).reduce(
      (sum, details) => sum + (details?.relatedPayments?.length || 0),
      0
    );
    return [
      {
        label: 'Total Formateurs',
        value: String(summaryStats.totalTeachers),
        icon: AcademicCapIcon,
        iconWrap: 'bg-blue-500/15 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200',
      },
      {
        label: 'Revenus Totaux',
        value: `${Math.round(summaryStats.totalRevenue).toLocaleString('fr-FR')} DA`,
        icon: BanknotesIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        label: 'Revenu Moyen',
        value: `${Math.round(summaryStats.averageRevenue).toLocaleString('fr-FR')} DA`,
        icon: StarIcon,
        iconWrap: 'bg-violet-500/15 text-violet-800 dark:bg-violet-500/20 dark:text-violet-100',
      },
      {
        label: 'Paiements Liés',
        value: String(linkedPayments),
        subtext: 'Voir détails',
        subtextHref: '/payments',
        icon: CreditCardIcon,
        iconWrap: 'bg-orange-500/15 text-orange-800 dark:bg-orange-500/20 dark:text-orange-100',
      },
      {
        label: 'Top Performer',
        value: summaryStats.topEarner?.name || 'N/A',
        subtext: summaryStats.topEarner
          ? `${Math.round(summaryStats.topEarner.earnings).toLocaleString('fr-FR')} DA`
          : null,
        icon: UserGroupIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [summaryStats, teacherPaymentDetails]);

  // Spécialités disponibles pour le filtre
  const specialties = React.useMemo(() => {
    const defaultSpecialties = [
      { value: 'all', label: 'Toutes les spécialités' }
    ];
    
    if (!specializationsData?.data || specializationsData.data.length === 0) {
      return [
        ...defaultSpecialties,
        { value: 'Développement Web', label: 'Développement Web' },
        { value: 'Design Graphique', label: 'Design Graphique' },
        { value: 'Marketing Digital', label: 'Marketing Digital' },
        { value: 'Bureautique', label: 'Bureautique' },
        { value: 'Langue', label: 'Langues' }
      ];
    }
    
    return [
      ...defaultSpecialties,
      ...specializationsData.data.map(spec => ({
        value: spec.value,
        label: spec.name
      }))
    ];
  }, [specializationsData]);

  // Statuts disponibles pour le filtre
  const statuses = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actif' },
    { value: 'vacation', label: 'En congé' },
    { value: 'inactive', label: 'Inactif' }
  ];

  // Gestion de l'ajout/modification de formateur
  const handleSaveTeacher = async (teacherData) => {
    try {
      if (teacherData.id) {
        await updateTrainer(teacherData);
        toast.success('Formateur modifié avec succès');
      } else {
        await addTrainer({
          ...teacherData,
          createdAt: new Date().toISOString()
        });
        toast.success('Formateur ajouté avec succès');
      }

      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving teacher:', err);
      toast.error('Erreur lors de l\'enregistrement du formateur');
    }
  };

  // Gestion de la suppression de formateur
  const handleDeleteTeacher = async (id) => {
    try {
      await deleteTrainerItem(id);
      toast.success('Formateur supprimé avec succès');
      setIsDeleteModalOpen(false);
      setTeacherToDelete(null);
    } catch (err) {
      console.error('Error deleting teacher:', err);
      toast.error('Erreur lors de la suppression du formateur');
    }
  };

  // Fonction pour ouvrir la modal de suppression
  const openDeleteModal = (teacher) => {
    setTeacherToDelete(teacher);
    setIsDeleteModalOpen(true);
  };

  // Fonction pour ouvrir le formulaire de modification
  const openEditForm = (teacher) => {
    setSelectedTeacher(teacher);
    setIsFormOpen(true);
  };

  if (error) {
    return (
      <div className="bg-red-900/20 backdrop-blur-sm rounded-xl p-6 border border-red-500/20">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Impossible de charger les données des formateurs</h3>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Une erreur s'est produite lors de la récupération des données. 
              Veuillez vérifier votre connexion ou réessayer plus tard.
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button 
            onClick={() => refreshData()}
            className="px-4 py-2 bg-blue-600 rounded-lg text-slate-900 dark:text-slate-50 flex items-center gap-2"
          >
            <ArrowPathIcon className="h-5 w-5" /> 
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Check for missing data and provide guidance
  const hasRealTrainers = teachers.length > 0;
  const hasRealCourses = courses.length > 0;
  const hasRealPayments = payments.length > 0;
  
  if (!hasRealTrainers || !hasRealCourses || !hasRealPayments) {
    return (
      <div className="bg-yellow-900/20 backdrop-blur-sm rounded-xl p-6 border border-yellow-500/20">
        <div className="flex items-center gap-3 mb-4">
          <ExclamationTriangleIcon className="h-10 w-10 text-yellow-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Données insuffisantes pour calculer les revenus</h3>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Les revenus des formateurs ne peuvent pas être calculés sans toutes les données nécessaires.
            </p>
          </div>
        </div>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${hasRealTrainers ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-slate-700 dark:text-slate-300">
              {hasRealTrainers ? '✓' : '✗'} Formateurs ({teachers.length})
            </span>
            {!hasRealTrainers && (
              <Link 
                to="/trainers"
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                Ajouter des formateurs
              </Link>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${hasRealCourses ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-slate-700 dark:text-slate-300">
              {hasRealCourses ? '✓' : '✗'} Cours ({courses.length})
            </span>
            {!hasRealCourses && (
              <Link 
                to="/courses"
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                Ajouter des cours
              </Link>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${hasRealPayments ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-slate-700 dark:text-slate-300">
              {hasRealPayments ? '✓' : '✗'} Paiements ({payments.length})
            </span>
            {!hasRealPayments && (
              <Link 
                to="/payments"
                className="text-blue-400 hover:text-blue-300 text-sm underline"
              >
                Ajouter des paiements
              </Link>
            )}
          </div>
        </div>
        
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h4 className="text-slate-900 dark:text-slate-50 font-medium mb-2">Pour voir les revenus des formateurs:</h4>
          <ol className="text-slate-600 dark:text-slate-400 text-sm space-y-1 list-decimal list-inside">
            <li>Ajoutez des formateurs via la page Formateurs</li>
            <li>Créez des cours et assignez-les aux formateurs</li>
            <li>Enregistrez des paiements liés aux cours</li>
          </ol>
        </div>
        
        <div className="mt-4 flex gap-3">
          <Link 
            to="/trainers"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Formateurs
          </Link>
          <Link 
            to="/courses"
            className="px-4 py-2 bg-forma-600 text-white rounded-lg hover:bg-forma-500 transition-colors flex items-center gap-2"
          >
            <AcademicCapIcon className="h-4 w-4" />
            Cours
          </Link>
          <Link 
            to="/payments"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <CreditCardIcon className="h-4 w-4" />
            Paiements
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/75"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{card.label}</p>
                <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-50 truncate">{card.value}</p>
                {card.subtextHref ? (
                  <Link
                    to={card.subtextHref}
                    className="mt-1 inline-flex items-center text-xs font-medium text-orange-700 hover:text-orange-800 dark:text-orange-300 dark:hover:text-orange-200"
                  >
                    <CreditCardIcon className="h-3 w-3 mr-1 shrink-0" />
                    {card.subtext}
                  </Link>
                ) : card.subtext ? (
                  <p className="mt-1 text-xs font-semibold text-amber-800 dark:text-amber-200">{card.subtext}</p>
                ) : null}
              </div>
              <div
                className={clsx(
                  'shrink-0 rounded-xl p-2.5 ring-1 ring-slate-200/80 dark:ring-slate-700',
                  card.iconWrap
                )}
              >
                <card.icon className="h-7 w-7" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Barre de recherche et filtres organisée */}
      <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-4 lg:p-6 space-y-4">
        {/* Ligne 1: Recherche et actions */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center px-4 py-3 bg-slate-50 dark:bg-slate-800/80 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-blue-500/30 transition-colors w-full sm:w-80">
              <MagnifyingGlassIcon className="h-5 w-5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
          <input
            type="text"
                className="ml-3 bg-transparent outline-none text-slate-900 dark:text-slate-50 w-full placeholder:text-slate-400 dark:text-slate-500"
            placeholder="Rechercher un formateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
            <div className="flex items-center text-xs text-green-400 bg-green-400/10 px-3 py-2 rounded-lg border border-green-400/20 whitespace-nowrap">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
              <span>Données temps réel</span>
            </div>
          </div>
          
          {/* Actions principales */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link 
              to="/payments"
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg flex items-center justify-center gap-2 hover:from-orange-700 hover:to-orange-800 transition-all duration-200 shadow-lg hover:shadow-orange-500/25"
            >
              <CreditCardIcon className="h-5 w-5" />
              <span className="font-medium hidden sm:inline">Paiements</span>
              <span className="font-medium sm:hidden">Paiem.</span>
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Link>

            <button 
              onClick={() => {
                setSelectedTeacher(null);
                setIsFormOpen(true);
              }}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 bg-forma-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-forma-500 transition-all duration-200 shadow-md"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="font-medium">Ajouter</span>
            </button>
          </div>
        </div>

        {/* Ligne 2: Filtres */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-600">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 flex-shrink-0">
            <FunnelIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Filtres:</span>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative">
          <select
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
                  className="appearance-none w-full sm:w-auto px-4 py-2 pr-10 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg outline-none border border-slate-200 dark:border-slate-600 hover:border-purple-500/30 focus:border-purple-500/50 transition-colors cursor-pointer"
          >
            {specialties.map((specialty) => (
                    <option key={specialty.value} value={specialty.value} className="bg-slate-800 text-slate-900 dark:text-slate-50">
                {specialty.label}
              </option>
            ))}
          </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              
              <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none w-full sm:w-auto px-4 py-2 pr-10 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg outline-none border border-slate-200 dark:border-slate-600 hover:border-green-500/30 focus:border-green-500/50 transition-colors cursor-pointer"
          >
            {statuses.map((status) => (
                    <option key={status.value} value={status.value} className="bg-slate-800 text-slate-900 dark:text-slate-50">
                {status.label}
              </option>
            ))}
          </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Clear filters button */}
              {(searchTerm || specialtyFilter !== 'all' || statusFilter !== 'all') && (
          <button 
            onClick={() => {
                    setSearchTerm('');
                    setSpecialtyFilter('all');
                    setStatusFilter('all');
            }}
                  className="px-3 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 text-sm hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-lg transition-colors flex items-center justify-center gap-1 border border-slate-200 dark:border-slate-600 hover:border-red-500/30"
                  title="Réinitialiser les filtres"
          >
                  <XMarkIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Effacer</span>
          </button>
              )}
            </div>
          </div>
          
          {/* Results count */}
          <div className="text-sm text-slate-600 dark:text-slate-400 bg-slate-100/80 dark:bg-slate-800/40 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 whitespace-nowrap">
            <span className="font-medium text-slate-900 dark:text-slate-50">{filteredTeachers.length}</span> formateur{filteredTeachers.length > 1 ? 's' : ''} trouvé{filteredTeachers.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Liste des formateurs */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeachers.map((teacher) => (
            <motion.div
              key={teacher.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600 hover:border-blue-500/30 transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    {teacher.photo ? (
                      <img 
                        src={teacher.photo} 
                        alt={teacher.name} 
                        className="h-16 w-16 rounded-full object-cover border-2 border-blue-500/30" 
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                        <AcademicCapIcon className="h-8 w-8 text-slate-600 dark:text-slate-400" />
                      </div>
                    )}
                    
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{teacher.name}</h3>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        {specialties.find(s => s.value === teacher.specialty)?.label || teacher.specialty}
                      </p>
                      <div className="flex items-center mt-1">
                        <StarIcon className="h-4 w-4 text-yellow-400" />
                        <span className="ml-1 text-slate-600 dark:text-slate-400 text-sm">{teacher.rating}</span>
                        <span className="mx-2 text-slate-900 dark:text-slate-50/30">•</span>
                        <span className="text-slate-600 dark:text-slate-400 text-sm">{teacher.experience} ans exp.</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1">
                    <button 
                      onClick={() => openEditForm(teacher)}
                      className="p-1.5 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </button>
                    <button 
                      onClick={() => openDeleteModal(teacher)}
                      className="p-1.5 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-red-500/20 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 text-slate-600 dark:text-slate-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-4 w-4 text-blue-400" />
                    <span className="ml-2 text-slate-600 dark:text-slate-400 text-sm">{teacher.students} étudiants</span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 text-green-400" />
                    <span className="ml-2 text-slate-600 dark:text-slate-400 text-sm">{teacher.courses} cours</span>
                  </div>
                </div>

                {/* Teacher Earnings Section */}
                <div className="mt-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <BanknotesIcon className="h-4 w-4 text-green-400" />
                      <span className="ml-2 text-slate-600 dark:text-slate-400 text-sm">Revenus générés</span>
                    </div>
                    <span className="text-green-400 font-medium text-sm">
                      {Math.round(teacher.earnings).toLocaleString('fr-FR')} DA
                    </span>
                  </div>
                  
                  {/* Earnings Breakdown */}
                  {teacherPaymentDetails[teacher.id] && (
                    <div className="space-y-1">
                      {teacherPaymentDetails[teacher.id].totalCommissions > 0 && (
                        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                          <span>• Commissions: {teacherPaymentDetails[teacher.id].paymentCommissions.length} paiements</span>
                          <span>{Math.round(teacherPaymentDetails[teacher.id].totalCommissions).toLocaleString('fr-FR')} DA</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Link to Payments Page */}
                  {teacherPaymentDetails[teacher.id]?.relatedPayments.length > 0 && (
                    <Link 
                      to="/payments" 
                      className="mt-2 flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <CreditCardIcon className="h-3 w-3 mr-1" />
                      <span>Voir les paiements liés ({teacherPaymentDetails[teacher.id].relatedPayments.length})</span>
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </Link>
                  )}
                </div>

                <div className="mt-4 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                  {teacher.bio}
                </div>

                <div className="mt-4 flex justify-end">
                  <button 
                    onClick={() => setSelectedTeacher(teacher)}
                    className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Voir détails
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!isLoading && filteredTeachers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <AcademicCapIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20" />
          {teachers.length === 0 ? (
            <>
              <p className="mt-4 text-slate-500 dark:text-slate-400 text-lg">Aucun formateur n'a encore été ajouté</p>
              <p className="mt-2 text-slate-400 dark:text-slate-500 text-sm text-center">
                Les revenus des formateurs s'afficheront ici une fois que vous aurez:<br />
                • Ajouté des formateurs via la page Formateurs<br />
                • Créé des cours via la page Cours<br />
                • Enregistré des paiements via la page Paiements
              </p>
              <div className="mt-4 flex gap-3">
                <Link 
                  to="/trainers"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Ajouter un formateur
                </Link>
                <Link 
                  to="/courses"
                  className="px-4 py-2 bg-forma-600 text-white rounded-lg hover:bg-forma-500 transition-colors"
                >
                  Gérer les cours
                </Link>
              </div>
            </>
          ) : (
            <>
          <p className="mt-4 text-slate-500 dark:text-slate-400 text-lg">Aucun formateur ne correspond à votre recherche</p>
          <button 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setSpecialtyFilter('all');
            }}
            className="mt-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            Réinitialiser les filtres
          </button>
            </>
          )}
        </div>
      )}

      {/* Formulaire d'ajout/modification (modal) */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                {selectedTeacher ? 'Modifier le formateur' : 'Ajouter un formateur'}
              </h2>
              <button 
                onClick={() => setIsFormOpen(false)}
                className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800/80"
              >
                <XMarkIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Formulaire à implémenter */}
              <p className="text-slate-600 dark:text-slate-400">Formulaire d'ajout/modification à implémenter</p>
              
              <div className="flex justify-end mt-6 gap-2">
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/80"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleSaveTeacher({...selectedTeacher})}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {isDeleteModalOpen && teacherToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-2">Supprimer ce formateur</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Êtes-vous sûr de vouloir supprimer le formateur {teacherToDelete.name} ? Cette action est irréversible.
              </p>
              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/80"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleDeleteTeacher(teacherToDelete.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail du formateur */}
      {selectedTeacher && !isFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                Détails du formateur
              </h2>
              <button 
                onClick={() => setSelectedTeacher(null)}
                className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800/80"
              >
                <XMarkIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 mb-6">
              {selectedTeacher.photo ? (
                <img 
                  src={selectedTeacher.photo} 
                  alt={selectedTeacher.name} 
                  className="h-24 w-24 rounded-full object-cover border-2 border-blue-500/30" 
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                  <AcademicCapIcon className="h-12 w-12 text-slate-600 dark:text-slate-400" />
                </div>
              )}
              
              <div>
                <h3 className="text-xl font-medium text-slate-900 dark:text-slate-50">{selectedTeacher.name}</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {specialties.find(s => s.value === selectedTeacher.specialty)?.label || selectedTeacher.specialty}
                </p>
                <div className="flex items-center mt-1">
                  <StarIcon className="h-4 w-4 text-yellow-400" />
                  <span className="ml-1 text-slate-600 dark:text-slate-400">{selectedTeacher.rating}</span>
                  <span className="mx-2 text-slate-900 dark:text-slate-50/30">•</span>
                  <span className="text-slate-600 dark:text-slate-400">{selectedTeacher.experience} ans d'expérience</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-100/80 dark:bg-slate-800/40 p-3 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Email</p>
                <p className="text-slate-900 dark:text-slate-50">{selectedTeacher.email}</p>
              </div>
              <div className="bg-slate-100/80 dark:bg-slate-800/40 p-3 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Téléphone</p>
                <p className="text-slate-900 dark:text-slate-50">{selectedTeacher.phone}</p>
              </div>
              <div className="bg-slate-100/80 dark:bg-slate-800/40 p-3 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Date d'embauche</p>
                <p className="text-slate-900 dark:text-slate-50">{new Date(selectedTeacher.joinDate).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="bg-slate-100/80 dark:bg-slate-800/40 p-3 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Salaire</p>
                <p className="text-slate-900 dark:text-slate-50">{selectedTeacher.salary.toLocaleString('fr-DZ')} DA / mois</p>
              </div>
            </div>
            
            <div className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-2">Bio</h4>
              <p className="text-slate-600 dark:text-slate-400">{selectedTeacher.bio}</p>
            </div>
            
            <div className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-2">Disponibilité</h4>
              <div className="flex flex-wrap gap-2">
                {selectedTeacher.availability.map((day) => (
                  <span key={day} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                    {day}
                  </span>
                ))}
              </div>
            </div>

            {/* Detailed Revenue Breakdown */}
            {teacherPaymentDetails[selectedTeacher.id] && (
              <div className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">Détail des Revenus</h4>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">
                      {Math.round(selectedTeacher.earnings).toLocaleString('fr-FR')} DA
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Total généré</p>
                  </div>
                </div>

                {/* Payment Commissions */}
                {teacherPaymentDetails[selectedTeacher.id].paymentCommissions.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-md font-medium text-slate-900 dark:text-slate-50 flex items-center">
                        <BanknotesIcon className="h-4 w-4 mr-2 text-green-400" />
                        Commissions sur Paiements
                      </h5>
                      <span className="text-green-400 font-medium">
                        {Math.round(teacherPaymentDetails[selectedTeacher.id].totalCommissions).toLocaleString('fr-FR')} DA
                      </span>
                    </div>
                    
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {teacherPaymentDetails[selectedTeacher.id].paymentCommissions.map((commission, index) => (
                        <div key={index} className="bg-slate-100/80 dark:bg-slate-800/40 p-2 rounded-lg text-sm">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-slate-900 dark:text-slate-50 font-medium">{commission.studentName}</p>
                              <p className="text-slate-600 dark:text-slate-400 text-xs">{commission.courseName}</p>
                              <p className="text-slate-400 dark:text-slate-500 text-xs">
                                Réf: {commission.reference} • 
                                {commission.date && new Date(commission.date).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-green-400 font-medium">
                                +{Math.round(commission.commission).toLocaleString('fr-FR')} DA
                              </p>
                              <p className="text-slate-400 dark:text-slate-500 text-xs">
                                {((commissionRate) * 100).toFixed(1)}% de {Math.round(commission.amount).toLocaleString('fr-FR')} DA
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sessions are for scheduling only - no separate revenue */}

                {/* Navigation to Payments */}
                <div className="pt-3 border-t border-slate-200 dark:border-slate-600">
                  <div className="flex gap-3">
                    <Link 
                      to="/payments"
                      className="flex-1 px-4 py-2 bg-blue-600/20 text-blue-300 rounded-lg hover:bg-blue-600/30 transition-colors flex items-center justify-center gap-2"
                    >
                      <CreditCardIcon className="h-4 w-4" />
                      <span>Voir tous les paiements</span>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </Link>
                    
                    {teacherPaymentDetails[selectedTeacher.id].relatedPayments.length > 0 && (
                      <Link 
                        to={`/payments?teacher=${encodeURIComponent(selectedTeacher.name)}`}
                        className="flex-1 px-4 py-2 bg-green-600/20 text-green-300 rounded-lg hover:bg-green-600/30 transition-colors flex items-center justify-center gap-2"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>Filtrer ses paiements ({teacherPaymentDetails[selectedTeacher.id].relatedPayments.length})</span>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => {
                  setSelectedTeacher(null);
                  openEditForm(selectedTeacher);
                }}
                className="px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/80 flex items-center gap-1"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Modifier</span>
              </button>
              <button 
                onClick={() => {
                  setSelectedTeacher(null);
                  openDeleteModal(selectedTeacher);
                }}
                className="px-4 py-2 bg-red-600/30 text-red-300 rounded-lg hover:bg-red-600/50 flex items-center gap-1"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeachersList;